import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { Divider } from "@mui/material";

import { ConfirmationDialog } from "@framework/mui-dialog-confirmation";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { TextInput, PasswordInput, SelectInput } from "@framework/mui-inputs-core";
import { FormDialog } from "@framework/mui-dialog-form";
import { IUserItem, IUserPassword, UserGroup } from "@framework/types";
import { useApiCall } from "@framework/react-hooks";
import { DEFAULT_PASSWORD_POLICY, parsePasswordPolicy, IPasswordPolicy } from "@framework/yup-rules";

import { createUserValidationSchema, createChangePasswordValidationSchema } from "./validation";

export const usePasswordPolicy = () => {
  const [policy, setPolicy] = useState<IPasswordPolicy>();
  const { fn: loadPolicy } = useApiCall(
    async api => {
      const data = (await api.fetchJson({ url: "/auth/plc" })) as { plc?: unknown } | undefined;
      return parsePasswordPolicy(data?.plc);
    },
    { success: false, error: false },
  );

  useEffect(() => {
    let cancelled = false;

    loadPolicy()
      .then(nextPolicy => {
        if (!cancelled && nextPolicy) {
          setPolicy(nextPolicy);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPolicy(DEFAULT_PASSWORD_POLICY);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return policy;
};

export interface IUserCreateDialogProps {
  open: boolean;
  onCancel: (form?: any) => void;
  onConfirm: (values: Partial<IUserItem> & { email: string | null }, form: any) => Promise<void>;
  initialValues: IUserItem;
  policy?: IPasswordPolicy;
}

export interface IUserChangePasswordDialogProps {
  open: boolean;
  onCancel: (form?: any) => void;
  onConfirm: (values: Partial<IUserPassword>, form: any) => Promise<void>;
  initialValues: IUserPassword;
  policy?: IPasswordPolicy;
}

export interface IUserChangeStatusDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (item: any) => Promise<void>;
  getTitle?: (item: any) => string;
  initialValues: IUserItem;
}

export const UserCreateDialog: FC<IUserCreateDialogProps> = props => {
  const { initialValues, policy, ...rest } = props;
  const { name, surName, email, login } = initialValues;
  const fixedValues = {
    login,
    password: "",
    userGroup: "",
    name,
    surName,
    email,
    webKey: "",
  };

  const userGroupOptions = Object.fromEntries(
    Object.entries(UserGroup)
      .filter(
        ([, value]) =>
          typeof value === "number" &&
          UserGroup[value as number] !== "ADMINISTRATOR" &&
          UserGroup[value as number] !== "TECHNICAL_USER",
      )
      .map(([_key, value]) => [value.toString(), value.toString()]),
  ) as Record<string, string>;

  return (
    <FormDialog
      key={policy ? "ready" : "loading"}
      disabled={policy ? undefined : true}
      initialValues={fixedValues}
      validationSchema={policy ? createUserValidationSchema(policy) : undefined}
      message="dialogs.createUser"
      testId="UserCreateForm"
      maxWidth="md"
      {...rest}
    >
      <TextInput name="login" />
      <PasswordInput name="password" autoComplete="new-password" />
      <br />
      <Divider />
      <br />
      <SelectInput name="userGroup" options={userGroupOptions} />
      <br />
      <TextInput name="name" />
      <TextInput name="surName" />
      <TextInput name="email" />
      <br />
      <Divider />
      <br />
      <TextInput name="webKey" />
    </FormDialog>
  );
};

export const UserChangePasswordDialog: FC<IUserChangePasswordDialogProps> = props => {
  const { initialValues, policy, ...rest } = props;
  const { id, login } = initialValues;
  const fixedValues = {
    id,
    login,
    newPassword: "",
    confirmPassword: "",
  };

  return (
    <FormDialog
      key={policy ? "ready" : "loading"}
      disabled={policy ? undefined : true}
      initialValues={fixedValues}
      validationSchema={policy ? createChangePasswordValidationSchema(policy) : undefined}
      message="dialogs.setNewPassword"
      testId="PasswordForm"
      maxWidth="sm"
      {...rest}
    >
      <PasswordInput name="newPassword" autoComplete="new-password" />
      <PasswordInput name="confirmPassword" autoComplete="new-password" />
    </FormDialog>
  );
};

export const UserChangeStatusDialog: FC<IUserChangeStatusDialogProps> = props => {
  const { initialValues, onConfirm, getTitle, ...rest } = props;
  const { id, isActive } = initialValues;
  const fixedValues = {
    id,
    isActive: !isActive,
  };

  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async (): Promise<void> => {
    setIsLoading(true);
    await onConfirm(fixedValues);
    setIsLoading(false);
  };

  const handleSyncConfirm = (): void => {
    handleConfirm().catch(error => {
      console.error("Error during confirmation:", error);
    });
  };

  return (
    <ConfirmationDialog maxWidth="xs" onConfirm={handleSyncConfirm} data-testid="UserChangeStatusDialog" {...rest}>
      <ProgressOverlay isLoading={isLoading}>
        <FormattedMessage
          id={isActive ? "dialogs.deactivate" : "dialogs.activate"}
          values={{
            title: getTitle ? getTitle(initialValues) : initialValues.name,
          }}
        />
      </ProgressOverlay>
    </ConfirmationDialog>
  );
};
